import { useEffect, useRef } from 'react';

interface UseSwipeToCloseOptions {
  /** Включён ли listener (как правило isMobile && isOpen). */
  enabled: boolean;
  /** Колбэк когда жест свайпа вниз пройден. */
  onClose: () => void;
  /** Расстояние свайпа в пикселях для срабатывания. Default 80. */
  threshold?: number;
}

/**
 * Свайп-вниз для закрытия bottom-sheet'ов на мобильных.
 * Прикрепляет handlers к переданному ref (обычно — drag-handle или header листа).
 *
 * Использование:
 *   const handleRef = useSwipeToClose({ enabled: isMobile && isOpen, onClose });
 *   <span ref={handleRef} className="drag-handle" />
 */
export function useSwipeToClose<T extends HTMLElement = HTMLElement>(
  options: UseSwipeToCloseOptions,
) {
  const { enabled, onClose, threshold = 80 } = options;
  const elRef = useRef<T | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!enabled || !el) return;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) { startRef.current = null; return; }
      startRef.current = { x: t.clientX, y: t.clientY };
    };
    const onTouchMove = (e: TouchEvent) => {
      const start = startRef.current;
      if (!start) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - start.y;
      const dx = t.clientX - start.x;
      // Только вертикальный жест вниз: |dy| должно быть в 1.5× больше |dx|.
      // Защита от ложного срабатывания при горизонтальном свайпе или скролле в стороны.
      if (dy > threshold && Math.abs(dy) > Math.abs(dx) * 1.5) {
        startRef.current = null;
        onClose();
      }
    };
    const onTouchEnd = () => {
      startRef.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, onClose, threshold]);

  return elRef;
}
